import { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Users, QrCode, Smartphone, ChevronDown, CreditCard } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { format, differenceInDays, startOfToday } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { EXTRA_BED_NOTE } from '../lib/roomSettings';
import { apiErrorMessage } from '../lib/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '919649611111';

// Room images are stored as relative /api/uploads/... paths; on the dev server
// (port 3000) they'd resolve to the wrong host. Prefix with the backend URL.
const normalizeUrl = (url) =>
  url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

// Online payment (Razorpay) is fully wired end to end. Keep false until the live
// RAZORPAY_KEY_ID/SECRET are on the prod backend — a visible Pay Online button
// that 503s would cost bookings. Flip to true + rebuild once keys are in place.
// Online payment is LIVE — backend has the live Razorpay keys in
// .env.production (deployed alongside this build). If payments must be
// paused, flip to false and rebuild.
const RAZORPAY_ENABLED = true;

const BOOKING_FAQS = [
  {
    q: "Do I need to create an account to book?",
    a: "No account needed. Just enter your name, email, and phone and you're done — takes under a minute. Signing in is optional if you'd like to save your details and view your booking history later."
  },
  {
    q: "What payment methods are accepted?",
    a: RAZORPAY_ENABLED
      ? "Pay online instantly via Razorpay (Cards, UPI, Netbanking, Wallets) for immediate confirmation. We also accept Direct UPI transfers and Pay at Resort (Cash/UPI on arrival) — for those, you'll be redirected to WhatsApp where we confirm your reservation."
      : "We accept Direct UPI transfers and Pay at Resort (Cash/UPI on arrival). After booking, you'll be redirected to WhatsApp where we confirm your reservation and share payment details. Online card payment is coming soon."
  },
  {
    q: "What is the cancellation policy?",
    a: "Full details are on our Refund & Cancellation Policy page. In short: free cancellation up to 7 days before check-in (less gateway charges), 50% refund between 3–7 days, and no refund within 72 hours or for no-shows. Peak and festival dates may differ. Email info@rudragarh.com to cancel or amend."
  },
  {
    q: "Can I request early check-in or late check-out?",
    a: "Early check-in (before 11:00 AM) and late check-out (after 10:00 AM) are available on request, subject to availability. Please mention your requirement in the special requests field during booking or contact us directly."
  },
];

const buildWhatsAppMessage = (booking, paymentMethod) => {
  const checkIn = new Date(booking.check_in).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const checkOut = new Date(booking.check_out).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  if (paymentMethod === 'upi') {
    return `Hi! I'm making a UPI payment for my booking at Rudragarh Resort.

*Booking ID:* ${booking.id.slice(0, 8).toUpperCase()}
*Room:* ${booking.room_name}
*Check-in:* ${checkIn}
*Check-out:* ${checkOut}
*Guests:* ${booking.guests}
*Amount:* ₹${booking.total_amount.toLocaleString()}

*Guest Name:* ${booking.guest_name}
*Phone:* ${booking.guest_phone}

Please share your UPI details so I can complete the payment and confirm my booking. Thank you!`;
  }

  // COD / Pay at Resort
  return `Hi! I'd like to confirm my booking at Rudragarh Resort.

*Booking ID:* ${booking.id.slice(0, 8).toUpperCase()}
*Room:* ${booking.room_name}
*Check-in:* ${checkIn}
*Check-out:* ${checkOut}
*Guests:* ${booking.guests}
*Total:* ₹${booking.total_amount.toLocaleString()}

*Guest Name:* ${booking.guest_name}
*Phone:* ${booking.guest_phone}
*Email:* ${booking.guest_email}
*Payment:* Pay at Resort (Cash/UPI on arrival)

Please confirm my reservation. Thank you!`;
};

const BookNow = () => {
  const [searchParams] = useSearchParams();
  const { bookingId } = useParams(); // /booking/:bookingId — resume payment from email
  const { user } = useAuth();

  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [guests, setGuests] = useState(1);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Guest Details
  const [guestDetails, setGuestDetails] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    specialRequests: '',
  });

  // Payment
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [booking, setBooking] = useState(null);

  // Resume-payment mode: load the booking from the email link and jump
  // straight to the pay screen (or to confirmation if it's already paid).
  useEffect(() => {
    if (!bookingId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bookings/${bookingId}`);
        setBooking(data);
        setStep(data.payment_status === 'paid' ? 3 : 'pay');
      } catch (e) {
        toast.error('Booking not found. Please contact us on WhatsApp.');
      }
    })();
  }, [bookingId]);

  useEffect(() => {
    fetchRooms();

    // Pre-fill from URL params
    const roomId = searchParams.get('room');
    const checkInParam = searchParams.get('checkIn');
    const checkOutParam = searchParams.get('checkOut');
    const guestsParam = searchParams.get('guests');

    if (checkInParam) setCheckIn(new Date(checkInParam));
    if (checkOutParam) setCheckOut(new Date(checkOutParam));
    if (guestsParam) setGuests(Number(guestsParam));

    if (roomId) {
      // We'll set selected room after rooms are fetched
      setStep(2);
    }
  }, [searchParams]);

  useEffect(() => {
    const roomId = searchParams.get('room');
    if (roomId && rooms.length > 0) {
      const room = rooms.find(r => r.id === roomId);
      if (room) setSelectedRoom(room);
    }
  }, [rooms, searchParams]);

  useEffect(() => {
    if (user) {
      setGuestDetails(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
      }));
    }
  }, [user]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;
  const totalPrice = selectedRoom ? nights * selectedRoom.price_per_night : 0;

  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
    setStep(2);
  };

  // Single-screen checkout: validate, create the booking, then hand off to
  // WhatsApp (or Razorpay when enabled) and land on the confirmation step.
  const handleReserve = async () => {
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    if (!guestDetails.name || !guestDetails.email || !guestDetails.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        room_id: selectedRoom.id,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        guests,
        guest_name: guestDetails.name,
        guest_email: guestDetails.email,
        guest_phone: guestDetails.phone,
        special_requests: guestDetails.specialRequests,
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      const created = response.data;
      setBooking(created);

      if (paymentMethod === 'razorpay') {
        handleRazorpayPayment(created); // opens gateway; sets confirmation step itself
        return;
      }
      openWhatsApp(created, paymentMethod);
      setStep(3);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create booking'));
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = (bkg, method) => {
    const message = buildWhatsAppMessage(bkg || booking, method || paymentMethod);
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`, '_blank');
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handleRazorpayPayment = async (bkg) => {
    const b = bkg || booking;
    if (!b) return;
    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error('Could not load the payment gateway. Please try another method.');
        return;
      }
      const { data: order } = await axios.post(`${API}/payments/razorpay/create-order`, { booking_id: b.id });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Rudragarh Resort',
        description: b.room_name,
        order_id: order.order_id,
        prefill: { name: b.guest_name, email: b.guest_email, contact: b.guest_phone },
        theme: { color: '#c9a84c' },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/razorpay/verify`, null, {
              params: {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
            });
            toast.success('Payment successful — your booking is confirmed!');
          } catch (e) {
            // Payment went through; the server-side webhook is the source of truth.
            toast.success('Payment received. We are confirming your booking now.');
          }
          setStep(3);
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Payment could not be started. Please try another method.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Book Your Stay | Rudragarh Resort, Jaisalmer</title>
        <meta name="description" content="Book your stay at Rudragarh Resort near Jaisalmer, Rajasthan. Choose your room, pick your dates, and confirm your desert getaway instantly." />
        <meta property="og:title" content="Book Your Stay | Rudragarh Resort" />
        <meta property="og:description" content="Reserve your desert escape at Rudragarh Resort — heritage rooms, cave stays, and more near Jaisalmer." />
        <meta property="og:url" content="https://www.rudragarh.com/book" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Book Your Stay | Rudragarh Resort" />
        <meta name="twitter:description" content="Reserve your desert escape at Rudragarh Resort — heritage rooms, cave stays, and more near Jaisalmer." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/book" />
      </Helmet>
      {/* Hero */}
      <section className="relative h-[30vh] flex items-center justify-center" data-testid="booking-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/resort-pond.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="overline">Reserve Your Experience</span>
            <h1 className="text-4xl md:text-5xl font-heading font-medium">
              Book <span className="text-gold-500">Now</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Progress Steps (hidden in resume-payment mode) */}
      {typeof step === 'number' && (
      <section className="py-8 border-b border-gold-500/10">
        <div className="max-w-4xl mx-auto container-padding">
          <div className="flex items-center justify-between">
            {['Select Room', 'Checkout', 'Confirmation'].map((label, index) => (
              <div key={label} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  step > index + 1 ? 'bg-gold-500 border-gold-500 text-black' :
                  step === index + 1 ? 'border-gold-500 text-gold-500' :
                  'border-foreground/20 text-foreground/40'
                }`}>
                  {step > index + 1 ? '✓' : index + 1}
                </div>
                <span className={`ml-2 hidden sm:block text-xs ${
                  step >= index + 1 ? 'text-foreground' : 'text-foreground/40'
                }`}>
                  {label}
                </span>
                {index < 2 && (
                  <div className={`w-8 md:w-16 h-0.5 mx-2 md:mx-4 ${
                    step > index + 1 ? 'bg-gold-500' : 'bg-foreground/20'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Booking Content */}
      <section className="section-padding" data-testid="booking-content">
        <div className="max-w-6xl mx-auto container-padding">
          {/* Resume payment (from the booking-received email link) */}
          {step === 'pay' && booking && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto text-center"
              data-testid="resume-payment"
            >
              <h2 className="text-2xl font-heading mb-2">
                Complete Your <span className="text-gold-500">Payment</span>
              </h2>
              <p className="text-foreground/60 mb-8 leading-relaxed">
                Your dates are being held. Pay securely via Razorpay and your booking is confirmed instantly.
              </p>
              <div className="card-glass p-6 text-left space-y-0 mb-8">
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Booking ID</span>
                  <span className="font-heading text-gold-500 tracking-wider">{booking.id.slice(0, 8).toUpperCase()}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Room</span>
                  <span className="text-sm font-medium">{booking.room_name}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Check-in</span>
                  <span className="text-sm">{new Date(booking.check_in).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Check-out</span>
                  <span className="text-sm">{new Date(booking.check_out).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Guests</span>
                  <span className="text-sm">{booking.guests}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-foreground/60 text-sm">Total Amount</span>
                  <span className="font-heading text-gold-500 text-lg">₹{booking.total_amount.toLocaleString()}</span>
                </div>
              </div>
              <button
                onClick={() => handleRazorpayPayment(booking)}
                disabled={loading}
                className="btn-primary w-full disabled:opacity-50"
                data-testid="resume-pay-button"
              >
                {loading ? 'Processing...' : `Pay ₹${booking.total_amount.toLocaleString()} Now`}
              </button>
              <button onClick={() => openWhatsApp(booking, 'upi')} className="btn-outline w-full mt-3">
                Confirm on WhatsApp instead
              </button>
            </motion.div>
          )}

          {/* Step 1: Select Room */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-2xl font-heading mb-8">Select Your <span className="text-gold-500">Room</span></h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rooms.map((room) => (
                  <div
                    key={room.id}
                    onClick={() => handleRoomSelect(room)}
                    className={`cursor-pointer group border transition-all ${
                      selectedRoom?.id === room.id
                        ? 'border-gold-500'
                        : 'border-gold-500/10 hover:border-gold-500/50'
                    }`}
                    data-testid={`select-room-${room.slug}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={normalizeUrl(room.images[0])}
                        alt={room.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 bg-void-subtle">
                      <h3 className="font-heading text-lg mb-1">{room.name}</h3>
                      <p className="text-foreground/60 text-sm mb-3 line-clamp-1">{room.short_description}</p>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-foreground/50 text-sm">
                          <Users size={14} />
                          <span>Up to {room.max_guests}</span>
                        </div>
                        <span className="text-gold-500 font-heading">
                          {room.original_price > room.price_per_night && (
                            <span className="text-foreground/40 line-through text-sm mr-1.5">₹{room.original_price.toLocaleString()}</span>
                          )}
                          ₹{room.price_per_night.toLocaleString()}/night
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Guest Details */}
          {step === 2 && selectedRoom && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Form */}
              <div className="lg:col-span-2 space-y-6">
                <h2 className="text-2xl font-heading mb-4">Guest <span className="text-gold-500">Details</span></h2>
                
                {/* Dates */}
                <div className="flex gap-4 text-xs text-foreground/40 mb-1">
                  <span>Check-in from 11:00 AM</span>
                  <span>•</span>
                  <span>Check-out by 10:00 AM (flexible if requested in advance)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Check-in *</label>
                    <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left bg-white/5 border-white/10 hover:bg-white/10"
                          data-testid="booking-checkin"
                        >
                          <Calendar className="mr-2 h-4 w-4 text-gold-500" />
                          {checkIn ? format(checkIn, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-void-paper border-gold-500/20" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={checkIn}
                          defaultMonth={checkIn || new Date()}
                          onSelect={(date) => {
                            setCheckIn(date);
                            if (date && checkOut && checkOut <= date) setCheckOut(null);
                            setCheckInOpen(false);
                            // flow straight into picking check-out
                            if (date) setTimeout(() => setCheckOutOpen(true), 150);
                          }}
                          disabled={(date) => date < startOfToday()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Check-out *</label>
                    <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left bg-white/5 border-white/10 hover:bg-white/10"
                          data-testid="booking-checkout"
                        >
                          <Calendar className="mr-2 h-4 w-4 text-gold-500" />
                          {checkOut ? format(checkOut, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-void-paper border-gold-500/20" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={checkOut}
                          defaultMonth={checkOut || checkIn || new Date()}
                          onSelect={(date) => { setCheckOut(date); setCheckOutOpen(false); }}
                          disabled={(date) => date <= (checkIn || new Date())}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-foreground/60 mb-2 block">Number of Guests *</label>
                  <select
                    value={guests}
                    onChange={(e) => setGuests(Number(e.target.value))}
                    className="w-full input-default px-4"
                    data-testid="booking-guests"
                  >
                    {Array.from({ length: selectedRoom.max_guests }, (_, i) => i + 1).map((num) => (
                      <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Full Name *</label>
                    <input
                      type="text"
                      value={guestDetails.name}
                      onChange={(e) => setGuestDetails({ ...guestDetails, name: e.target.value })}
                      className="w-full input-default px-4"
                      placeholder="John Doe"
                      data-testid="booking-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Email *</label>
                    <input
                      type="email"
                      value={guestDetails.email}
                      onChange={(e) => setGuestDetails({ ...guestDetails, email: e.target.value })}
                      className="w-full input-default px-4"
                      placeholder="john@example.com"
                      data-testid="booking-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-foreground/60 mb-2 block">Phone *</label>
                  <input
                    type="tel"
                    value={guestDetails.phone}
                    onChange={(e) => setGuestDetails({ ...guestDetails, phone: e.target.value })}
                    className="w-full input-default px-4"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="booking-phone"
                  />
                </div>

                <div>
                  <label className="text-sm text-foreground/60 mb-2 block">Special Requests</label>
                  <textarea
                    value={guestDetails.specialRequests}
                    onChange={(e) => setGuestDetails({ ...guestDetails, specialRequests: e.target.value })}
                    rows={3}
                    className="w-full input-default px-4 py-3 resize-none"
                    placeholder="Any special requests or requirements..."
                    data-testid="booking-requests"
                  />
                </div>

                {/* Payment method */}
                <div className="pt-2">
                  <label className="text-sm text-foreground/60 mb-3 block">Payment</label>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('cod')}
                      className={`w-full p-4 flex items-center gap-4 border transition-all ${
                        paymentMethod === 'cod' ? 'border-gold-500 bg-gold-500/10' : 'border-white/10 hover:border-gold-500/50'
                      }`}
                      data-testid="payment-cod"
                    >
                      <Smartphone className="text-gold-500" size={24} />
                      <div className="text-left">
                        <p className="font-heading">Pay at Resort</p>
                        <p className="text-sm text-foreground/60">Cash or UPI on arrival — confirm via WhatsApp</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentMethod('upi')}
                      className={`w-full p-4 flex items-center gap-4 border transition-all ${
                        paymentMethod === 'upi' ? 'border-gold-500 bg-gold-500/10' : 'border-white/10 hover:border-gold-500/50'
                      }`}
                      data-testid="payment-upi"
                    >
                      <QrCode className="text-gold-500" size={24} />
                      <div className="text-left">
                        <p className="font-heading">Direct UPI Transfer</p>
                        <p className="text-sm text-foreground/60">Pay online — we'll share UPI details on WhatsApp</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => RAZORPAY_ENABLED && setPaymentMethod('razorpay')}
                      disabled={!RAZORPAY_ENABLED}
                      className={`w-full p-4 flex items-center gap-4 border transition-all ${
                        paymentMethod === 'razorpay' ? 'border-gold-500 bg-gold-500/10' : 'border-white/10'
                      } ${RAZORPAY_ENABLED ? 'hover:border-gold-500/50' : 'opacity-50 cursor-not-allowed'}`}
                      data-testid="payment-razorpay"
                    >
                      <CreditCard className="text-gold-500" size={24} />
                      <div className="text-left flex-1">
                        <p className="font-heading flex items-center gap-2">
                          Pay Online
                          {!RAZORPAY_ENABLED && (
                            <span className="text-[10px] uppercase tracking-wider bg-gold-500/20 text-gold-400 px-2 py-0.5 rounded">
                              Coming Soon
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-foreground/60">Cards, UPI, Netbanking &amp; Wallets — instant confirmation</p>
                      </div>
                    </button>
                  </div>

                  <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 text-sm text-foreground/70">
                    {paymentMethod === 'cod'
                      ? "Your booking details will be sent to our WhatsApp. We'll confirm your reservation and see you at the resort!"
                      : paymentMethod === 'upi'
                      ? "We'll share our UPI payment details on WhatsApp. Once you pay, send us the screenshot and we'll confirm your booking."
                      : "You'll pay securely via Razorpay — your booking is confirmed instantly."}
                  </div>
                </div>

                <div className="flex gap-4">
                  <button onClick={() => setStep(1)} className="btn-outline" data-testid="booking-back-step1">
                    Back
                  </button>
                  <button
                    onClick={handleReserve}
                    disabled={loading}
                    className="btn-primary flex-1 disabled:opacity-50"
                    data-testid="booking-reserve"
                  >
                    {loading ? 'Processing...' : paymentMethod === 'razorpay' ? 'Pay Now' : 'Reserve — Confirm on WhatsApp'}
                  </button>
                </div>
                <p className="text-xs text-foreground/40 text-center">
                  {paymentMethod === 'razorpay'
                    ? 'Secure payment via Razorpay. Your booking is confirmed the moment payment succeeds.'
                    : "You won't be charged today. We confirm your reservation on WhatsApp."}
                </p>
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <div className="sticky top-28 card-glass p-6 space-y-4">
                  <h3 className="font-heading text-xl text-gold-500">Booking Summary</h3>
                  <img src={normalizeUrl(selectedRoom.images[0])} alt={selectedRoom.name} className="w-full h-40 object-cover" />
                  <h4 className="font-heading text-lg">{selectedRoom.name}</h4>
                  
                  {nights > 0 && (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-foreground/60">Check-in</span>
                        <span>{format(checkIn, 'MMM d, yyyy')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground/60">Check-out</span>
                        <span>{format(checkOut, 'MMM d, yyyy')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground/60">Guests</span>
                        <span>{guests}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground/60">Nights</span>
                        <span>{nights}</span>
                      </div>
                      <div className="border-t border-gold-500/10 pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="text-foreground/60">₹{selectedRoom.price_per_night.toLocaleString()} × {nights} nights</span>
                          <span>₹{totalPrice.toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex justify-between font-heading text-lg pt-2">
                        <span>Total</span>
                        <span className="text-gold-500">₹{totalPrice.toLocaleString()}</span>
                      </div>
                      <p className="text-xs text-foreground/50 pt-1">
                        Complimentary breakfast included · {EXTRA_BED_NOTE}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && booking && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto text-center"
            >
              {/* Icon */}
              <div className="w-20 h-20 rounded-full border-2 border-gold-500 flex items-center justify-center mx-auto mb-6">
                <span className="text-gold-500 text-3xl">✓</span>
              </div>

              <h2 className="text-2xl font-heading mb-2">
                Booking Request <span className="text-gold-500">Received</span>
              </h2>
              <p className="text-foreground/60 mb-8 leading-relaxed">
                We've received your booking and sent a confirmation to <strong className="text-foreground">{booking.guest_email}</strong>.<br />
                Our team will confirm your reservation via WhatsApp or email shortly.
              </p>

              {/* Booking summary card */}
              <div className="card-glass p-6 text-left space-y-0 mb-8">
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Booking ID</span>
                  <span className="font-heading text-gold-500 tracking-wider">{booking.id.slice(0, 8).toUpperCase()}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Room</span>
                  <span className="text-sm font-medium">{booking.room_name}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Check-in</span>
                  <span className="text-sm">{new Date(booking.check_in).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Check-out</span>
                  <span className="text-sm">{new Date(booking.check_out).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gold-500/10">
                  <span className="text-foreground/60 text-sm">Guests</span>
                  <span className="text-sm">{booking.guests}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-foreground/60 text-sm">Total Amount</span>
                  <span className="font-heading text-gold-500 text-lg">₹{booking.total_amount.toLocaleString()}</span>
                </div>
              </div>

              {/* Status badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm mb-8">
                <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                Pending confirmation — we'll reach out soon
              </div>

              {/* WhatsApp nudge */}
              <div className="space-y-3">
                <p className="text-foreground/50 text-sm">Haven't heard from us? Tap below to follow up.</p>
                <button
                  onClick={() => openWhatsApp(booking, paymentMethod)}
                  className="btn-outline w-full"
                >
                  Open WhatsApp Again
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Need Help?</span>
            <h2 className="text-3xl font-heading font-medium">
              Booking <span className="text-gold-500">FAQs</span>
            </h2>
          </div>
          <div className="space-y-3">
            {BOOKING_FAQS.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-foreground/70 leading-relaxed border-t border-gold-500/10 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookNow;
